import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import ProfileSettings from '../components/settings/ProfileSettings';
import FamilySettings from '../components/settings/FamilySettings';
import OnboardingSettings from '../components/settings/OnboardingSettings';
import NotificationSettings from '../components/settings/NotificationSettings';
import PrivacySettings from '../components/settings/PrivacySettings';
import AppPreferences from '../components/settings/AppPreferences';
import AboutSettings from '../components/settings/AboutSettings';
import MyContributions from '../components/settings/MyContributions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUsers, faClipboardList, faBell, faShieldHalved, faGear, faInfoCircle, faHandHoldingHeart } from '@fortawesome/free-solid-svg-icons';
import { PageHeader } from '../design-system';

const SETTINGS_SECTIONS = [
  { id: 'profile', label: 'Profile', icon: faUser },
  { id: 'family', label: 'Family', icon: faUsers },
  { id: 'onboarding', label: 'Onboarding', icon: faClipboardList },
  { id: 'contributions', label: 'My Contributions', icon: faHandHoldingHeart },
  { id: 'notifications', label: 'Notifications', icon: faBell },
  { id: 'privacy', label: 'Privacy & Data', icon: faShieldHalved },
  { id: 'app', label: 'App Preferences', icon: faGear },
  { id: 'about', label: 'About', icon: faInfoCircle },
];

export default function Settings() {
  const [activeSection, setActiveSection] = useState('profile');
  const { user } = useAuth();
  const { family } = useFamily();

  const renderSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSettings />;
      case 'family':
        return <FamilySettings />;
      case 'onboarding':
        return <OnboardingSettings />;
      case 'contributions':
        return <MyContributions />;
      case 'notifications':
        return <NotificationSettings />;
      case 'privacy':
        return <PrivacySettings />;
      case 'app':
        return <AppPreferences />;
      case 'about':
        return <AboutSettings />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <div className="min-h-screen bg-background pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title="Settings"
        maxWidth="1280px"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Mobile: Tabs */}
        <div className="md:hidden mb-6 overflow-x-auto">
          <div className="flex gap-2 border-b border-border">
            {SETTINGS_SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  activeSection === section.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop: Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-6 sm:gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block">
            <nav className="bg-white rounded-card shadow-card border border-border p-2 sticky top-6">
              {SETTINGS_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                    activeSection === section.id
                      ? 'bg-primary text-white'
                      : 'text-text-primary hover:bg-surface'
                  }`}
                >
                  <FontAwesomeIcon icon={section.icon} className="w-4 h-4" />
                  <span className="font-medium">{section.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="min-w-0">
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
}
